"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useToast } from "@/contexts/toast-context";
import { createQuickFilter, deleteQuickFilter, replaceQuickFilter } from "@/lib/actions/saved-quick-filters";
import {
  MAX_SAVED_QUICK_FILTERS,
  sameQuickFilterName,
  withHiddenFields,
  type QuickFilterFields,
  type QuickFilterState,
  type SavedQuickFilter,
} from "@/lib/saved-quick-filters";
import { phCapture } from "@/utils/analytics";

export type SavedQuickFilters = {
  readonly filters: readonly SavedQuickFilter[];
  readonly available: boolean;
  readonly save: (name: string, state: QuickFilterState, fields: QuickFilterFields) => Promise<boolean>;
  readonly remove: (id: string) => Promise<boolean>;
};

const UNAVAILABLE: SavedQuickFilters = {
  filters: [],
  available: false,
  save: async () => false,
  remove: async () => false,
};

const UNDO_TOAST_MS = 6_000;
const FAILED = { success: false, error: undefined } as const;

const SavedQuickFiltersContext = createContext<SavedQuickFilters>(UNAVAILABLE);

function inCreatedOrder(filters: readonly SavedQuickFilter[]): SavedQuickFilter[] {
  return [...filters].sort((a, b) => a.createdAt - b.createdAt);
}

export function SavedQuickFiltersProvider({ initial, children }: { initial: readonly SavedQuickFilter[] | null; children: React.ReactNode }) {
  const { showToast } = useToast();
  const [available] = useState(initial !== null);
  const [filters, setFilters] = useState<readonly SavedQuickFilter[]>(initial ?? []);
  const held = useRef(filters);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const replace = useCallback((next: readonly SavedQuickFilter[]) => {
    held.current = next;
    setFilters(next);
  }, []);

  const serial = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    const run = queue.current.then(task);
    queue.current = run.catch(() => {});
    return run;
  }, []);

  const write = useCallback(async (filter: SavedQuickFilter, persist: () => ReturnType<typeof createQuickFilter>): Promise<boolean> => {
    const previous = held.current;
    const others = previous.filter((item) => item.id !== filter.id);
    replace(inCreatedOrder([...others, filter]));
    const result = await persist().catch(() => FAILED);
    if (result.success) {
      replace(inCreatedOrder([...others, result.filter]));
      return true;
    }
    replace(previous);
    showToast(result.error ?? "Try again.", "error", "Could not save filter");
    return false;
  }, [replace, showToast]);

  const save = useCallback((name: string, state: QuickFilterState, fields: QuickFilterFields) => serial(async () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return false;
    if (state.preset !== null && state.weights === null) {
      showToast(`The weights for ${state.preset} are not loaded. Choose the preset again, then save.`, "error", "Could not save filter");
      return false;
    }
    const existing = held.current.find((item) => sameQuickFilterName(item.name, trimmed));
    if (!existing && held.current.length >= MAX_SAVED_QUICK_FILTERS) {
      showToast("Delete one to save another.", "error", `Limit of ${MAX_SAVED_QUICK_FILTERS} saved filters`);
      return false;
    }
    const filter: SavedQuickFilter = existing
      ? { ...existing, name: trimmed, state: withHiddenFields(state, fields, existing.state) }
      : { id: crypto.randomUUID(), name: trimmed, createdAt: Date.now(), state };
    const saved = await write(filter, () => (existing ? replaceQuickFilter(filter, fields) : createQuickFilter(filter)));
    if (saved) phCapture("quick_filter_saved", { page: "optimizer", filter_count: held.current.length, replaced: existing !== undefined });
    return saved;
  }), [serial, write, showToast]);

  const remove = useCallback((id: string) => serial(async () => {
    const removed = held.current.find((item) => item.id === id);
    if (!removed) return true;
    const previous = held.current;
    replace(previous.filter((item) => item.id !== id));
    const result = await deleteQuickFilter(id).catch(() => FAILED);
    if (!result.success) {
      replace(previous);
      showToast(result.error ?? "Try again.", "error", "Could not delete filter");
      return false;
    }
    phCapture("quick_filter_deleted", { page: "optimizer", filter_count: held.current.length });
    showToast(removed.name, "info", "Filter deleted", UNDO_TOAST_MS, {
      label: "Undo",
      ariaLabel: `Restore ${removed.name}`,
      onClick: () => { serial(() => write(removed, () => createQuickFilter(removed))).catch(() => {}); },
    });
    return true;
  }), [serial, write, replace, showToast]);

  const value = useMemo<SavedQuickFilters>(
    () => ({ filters, available, save, remove }),
    [filters, available, save, remove],
  );

  return <SavedQuickFiltersContext.Provider value={value}>{children}</SavedQuickFiltersContext.Provider>;
}

export function useSavedQuickFilters(): SavedQuickFilters {
  return useContext(SavedQuickFiltersContext);
}
